<script lang="ts">
// the KPI rows and their header share this grid: expand, kind, name, meetmoment or onderwerp, type, delete
export const KPI_COLUMNS: string = '2rem 1.5rem minmax(14rem, 1fr) 11rem 10rem 2rem';
</script>

<script setup lang="ts">
import {defineAsyncComponent, nextTick, type PropType, ref, toRaw, watch} from 'vue';
import {KPI_CHANGE_EMIT, KPI_REMOVE_EMIT} from '@/base/cabConstants';
import {bevestigVerwijderen} from '@/base/utils';
import {
    CabKPI,
    type CabOrganization,
    CabTheme,
    DashboardReeksEnum,
    DashboardReeksEnum_name,
    diffDates,
    diffTimeSpans,
    enum_values,
    KPITypeEnum_name,
    OnderwerpEnum,
    OnderwerpEnum_name,
    PeriodEnum,
    PeriodEnum_name,
    PrognoseSchaalEnum,
    uuidOf,
    VoorspellingsSchaalEnum_name,
    WaardeTypeEnum,
    WaardeTypeEnum_name,
} from 'common';
import {
    type CascaderOption,
    ElCascader,
    ElCheckbox,
    ElCollapseTransition,
    ElDatePicker,
    ElInput,
    ElOption,
    ElSelect,
} from 'element-plus';
import RowGrid from '@/views/ui/RowGrid.vue';
import IconButton from '@/views/ui/IconButton.vue';
import Segmented, {type SegmentOption} from '@/views/ui/Segmented.vue';
import {ChartLine, ChevronRight, Flag, Trash2} from 'lucide-vue-next';
import type {ExpressionName} from '@/base/expressionLanguage';

const ExpressionEditorDialog = defineAsyncComponent(() => import('@/views/admin/ExpressionEditorDialog.vue'));

const emits = defineEmits([KPI_REMOVE_EMIT, KPI_CHANGE_EMIT]);
const props = defineProps({
    kpi: {
        type    : Object as PropType<CabKPI>,
        required: true,
    },
    allOrganizations: {
        type    : Object as PropType<CabOrganization[]>,
        required: true,
    },
    startExpanded: {
        type   : Boolean,
        default: false,
    },
    allKpiNames: {
        type   : Array as PropType<ExpressionName[]>,
        default: () => [],
    },
});
defineExpose({
    firstEdit,
});

const kpi                      = ref<CabKPI>(props.kpi);
const timeSpanHuman            = ref<[Date, Date]>(kpi.value.isMijlpaal() ? [new Date(), new Date()] : timeSpanToHuman(kpi.value.getTimeSpan()));
const eersteMeetMomentHuman    = ref<Date | undefined>(kpi.value.isMijlpaal() && kpi.value.getEersteMeetMoment() ? dateToHuman(kpi.value.getEersteMeetMoment()) : undefined);
const expanded                 = ref<boolean>(props.startExpanded);
const nameField                = ref<HTMLInputElement | null>(null);
const nameEditing              = ref<boolean>(false);
const descriptionField         = ref<HTMLInputElement | null>(null);
const descriptionEditing       = ref<boolean>(false);
const gehaaldExpressionField   = ref<HTMLInputElement | null>(null);
const gehaaldExpressionEditing = ref<boolean>(false);
const waardeExpressionField    = ref<HTMLInputElement | null>(null);
const waardeExpressionEditing  = ref<boolean>(false);
const orgThemeCombisAvailable  = ref(makeCascaderOptions(props.allOrganizations));
const orgThemeCombisSelected   = ref(props.kpi.getOrgThemeCombiUuids());
const instellingNames          = props.allOrganizations.map((o) => o.name);
const themaNames               = [...new Set(props.allOrganizations.flatMap((o) => o.themes.map((t) => t.name)))];

let isFirstEdit: boolean = false;

//==========================================
function firstEdit() {
    isFirstEdit = true;
    startEditingName();
}

watch(() => props.kpi,
    (newKpi) => {
        kpi.value.gehaaldExpressionErrors = newKpi.gehaaldExpressionErrors;
        kpi.value.waardeExpressionErrors  = newKpi.waardeExpressionErrors;
        if (!kpi.value.korteNaam) {
            // the backend generates a korte naam when the kpi is saved without one
            kpi.value.korteNaam = newKpi.korteNaam;
        }
    },
    {immediate: true},
);

watch(timeSpanHuman,
    (n) => {
        if (!kpi.value.isMijlpaal()) {
            if (!n) {
                if (kpi.value.clearTimeSpan()) {
                    onChange();
                }
            } else if (kpi.value.setTimeSpan(timeSpanFromHuman(n))) {
                onChange();
            }
            const newHumanSpan = timeSpanToHuman(kpi.value.getTimeSpan());
            if (!n || diffTimeSpans(n, newHumanSpan)) {
                timeSpanHuman.value = newHumanSpan;
            }
        }
    },
    {immediate: true},
);
watch(eersteMeetMomentHuman,
    (n) => {
        if (kpi.value.isMijlpaal()) {
            if (!n) {
                if (kpi.value.clearEersteMeetMoment()) {
                    onChange();
                }
            } else if (kpi.value.setEersteMeetMoment(dateFromHuman(n))) {
                onChange();
            }
            const newHumanDate = dateToHuman(kpi.value.getEersteMeetMoment());
            if (!n || diffDates(n, newHumanDate)) {
                eersteMeetMomentHuman.value = newHumanDate;
            }
        }
    },
    {immediate: true},
);

function makeCascaderOptions(allOrganizations: CabOrganization[]): CascaderOption[] {
    return allOrganizations.map((o) => ({
        label   : o.name,
        value   : uuidOf(o),
        children: o.themes.map((t) => ({
            label: t.name,
            value: uuidOf(t),
        })),
    }));
}

function timeSpanToHuman(v: Date[]): [Date, Date] {
    return [dateToHuman(v[0]), dateToHuman(v[1])];
}

function timeSpanFromHuman(v: Date[]): Date[] {
    if (v.length != 2 || !v[0] || !v[1]) {
        return CabKPI.initialTimeSpan(kpi.value.getReportingPeriod());
    } else {
        return [dateFromHuman(v[0]), dateFromHuman(v[1])];
    }
}

function dateToHuman(v: Date): Date {
    return new Date(v.getUTCFullYear(), v.getUTCMonth());
}

function dateFromHuman(v: Date): Date {
    if (!v) {
        return CabKPI.initialEersteMeetMoment();
    } else {
        return new Date(Date.UTC(v.getFullYear(), v.getMonth()));
    }
}

//==========================================
function startEditingName() {
    nameEditing.value = true;
    nextTick(() => nameField.value!.focus?.());
}

function stopEditingName() {
    nameEditing.value = false;
    nameField.value!.blur?.();
    if (isFirstEdit) {
        if (!kpi.value.name.trim()) {
            onDelete();
        }
        isFirstEdit = false;
    }
}

//==========================================
function startEditingDescription() {
    descriptionEditing.value = true;
    nextTick(() => descriptionField.value!.focus?.());
}

function stopEditingDescription() {
    descriptionEditing.value = false;
    descriptionField.value!.blur?.();
}

//==========================================
function startEditingGehaaldExpression() {
    gehaaldExpressionEditing.value = true;
    nextTick(() => gehaaldExpressionField.value!.focus?.());
}

function stopEditingGehaaldExpression() {
    gehaaldExpressionEditing.value = false;
    gehaaldExpressionField.value!.blur?.();
}

//==========================================
function startEditingWaardeExpression() {
    waardeExpressionEditing.value = true;
    nextTick(() => waardeExpressionField.value!.focus?.());
}

function stopEditingWaardeExpression() {
    waardeExpressionEditing.value = false;
    waardeExpressionField.value!.blur?.();
}

//==========================================
function onKorteNaamChange() {
    if (!kpi.value.korteNaam?.trim()) {
        kpi.value.korteNaam = undefined;
    }
    onChange();
}

//==========================================
async function onDeleteClick() {
    const soort: string = kpi.value.isMijlpaal() ? 'Mijlpaal' : 'KPI';
    if (await bevestigVerwijderen(kpi.value.name?.trim() ? `${soort} "${kpi.value.name}"` : `Deze ${soort.toLowerCase()}`)) {
        onDelete();
    }
}

function onDelete() {
    emits(KPI_REMOVE_EMIT, kpi.value);
}

function onChangeOrgTheme() {
    const rawSelected: string[][] = toRaw(orgThemeCombisSelected.value).map((path: string[]) => toRaw(path));
    const orgs: CabOrganization[] = [];
    const thms: CabTheme[]        = [];
    rawSelected.forEach(([oUuid, tUuid]) => {
        const org = props.allOrganizations.find((o) => uuidOf(o) === oUuid)!;
        const thm = org.themes.find((t) => uuidOf(t) === tUuid)!;
        orgs.push(org);
        thms.push(thm);
    });

    kpi.value.organizations = orgs;
    kpi.value.themes        = thms;
    onChange();
}

function onClickPeriod() {
    kpi.value.setReportingPeriod(kpi.value.getReportingPeriod() === PeriodEnum.Q ? PeriodEnum.H : PeriodEnum.Q);
    timeSpanHuman.value = timeSpanToHuman(kpi.value.getTimeSpan());
    onChange();
}

// the reporting period as a one-of choice; the count of periods sits in the label above it. PeriodEnum is numeric
// (Q = 1, H = 2) and Segmented works on strings
const PERIODE_OPTIES: SegmentOption[] = [
    {value: String(PeriodEnum.Q), label: PeriodEnum_name(PeriodEnum.Q)},
    {value: String(PeriodEnum.H), label: PeriodEnum_name(PeriodEnum.H)},
];

function onPeriodChange(periode: string) {
    if (periode !== String(kpi.value.getReportingPeriod())) {
        onClickPeriod();
    }
}

function onChange() {
    if (kpi.value.isMijlpaal()) {
        kpi.value.hasDoelwaarde = false;
        kpi.value.askKpiStatus  = false;
    }
    emits(KPI_CHANGE_EMIT, kpi.value);
}

function onDashboardReeksChange() {
    if (!kpi.value.dashboardReeks) {
        kpi.value.dashboardReeks = undefined;
    }
    onChange();
}

//==========================================
function dynExpressionTitle(fieldName: string, msgs: string | undefined): string {
    const part1 = `'${fieldName}' expressie`;
    const part2 = msgs ? `\n\n${msgs}` : '';

    return `${part1}${part2}`;
}

</script>

<template>
    <div class="border-b border-zzc-rand last:border-b-0">
        <RowGrid :columns="KPI_COLUMNS" class="!border-b-0">
            <IconButton
                :icon="ChevronRight"
                :label="expanded ? 'inklappen' : 'uitklappen'"
                size="sm"
                class="transition-transform"
                :class="{'rotate-90': expanded}"
                :aria-expanded="expanded ? 'true' : 'false'"
                data-testid="expand-button"
                @click="expanded = !expanded"
            />
            <span :title="kpi.isMijlpaal() ? 'mijlpaal' : 'KPI'" role="img" :aria-label="kpi.isMijlpaal() ? 'mijlpaal' : 'KPI'" class="text-zzc-muted inline-flex">
                <component :is="kpi.isMijlpaal() ? Flag : ChartLine" :size="16" :stroke-width="2"/>
            </span>
            <input
                class="row-input font-semibold"
                :class="{'row-input-on': nameEditing}"
                type="text"
                ref="nameField"
                v-model="kpi.name"
                @input="onChange"
                @click="startEditingName()"
                @focus="startEditingName()"
                @keyup.enter="stopEditingName()"
                @blur="stopEditingName()"
                placeholder="name"
                :title="kpi.name"
            />
            <el-date-picker
                v-if="kpi.isMijlpaal()"
                class="!w-full"
                v-model="eersteMeetMomentHuman"
                type="month"
                placeholder="kies een kwartaal"
                title="meetmoment"
                @change="onChange"
            />
            <el-select v-else v-model="kpi.onderwerp" placeholder="onderwerp" title="onderwerp" @change="onChange">
                <el-option
                    v-for="item in enum_values(OnderwerpEnum).filter((v: OnderwerpEnum) => v !== OnderwerpEnum.mijlpaal)"
                    :key="item"
                    :label="OnderwerpEnum_name(item)"
                    :value="item"
                />
            </el-select>
            <span class="text-sm truncate" title="type">{{ kpi.isMijlpaal() ? '' : KPITypeEnum_name(kpi.getKpiType()) }}</span>
            <IconButton :icon="Trash2" label="permanent verwijderen" variant="danger" size="sm" data-testid="delete-button" @click="onDeleteClick()"/>
        </RowGrid>

        <el-collapse-transition>
            <!-- indented to the name column (row padding + expand + icon + gaps), so the panel reads as part of its row -->
            <div v-if="expanded" class="ml-[5.5rem] mr-4 mb-3 p-3 rounded-control bg-zzc-lichtgrijs flex flex-col gap-3">
                <div class="flex flex-col gap-1" title="instelling/thema combi's voor deze KPI" data-testid="kpi-org-thema-select">
                    <span class="text-[11px] text-zzc-muted">instelling / thema</span>
                    <el-cascader class="w-full" v-model="orgThemeCombisSelected" :options="orgThemeCombisAvailable" :props="{multiple: true}" clearable placeholder="Selecteer instelling/themas" @change="onChangeOrgTheme"/>
                </div>

                <div class="flex flex-row flex-wrap gap-3">
                    <div v-if="!kpi.isMijlpaal()" class="flex flex-col gap-1 w-56" title="korte naam (alternatief voor de volledige naam in expressies)">
                        <span class="text-[11px] text-zzc-muted">korte naam</span>
                        <el-input v-model="kpi.korteNaam" @input="onKorteNaamChange" placeholder="korte naam"/>
                    </div>
                    <div class="flex flex-col gap-1 flex-1 min-w-[16rem]" title="omschrijving">
                        <span class="text-[11px] text-zzc-muted">omschrijving</span>
                        <el-input v-model="kpi.omschrijving" ref="descriptionField" @input="onChange" @click="startEditingDescription()" @focus="startEditingDescription()" @blur="stopEditingDescription()" type="textarea" placeholder="omschrijving"/>
                    </div>
                </div>

                <div v-if="!kpi.isMijlpaal()" class="flex flex-row flex-wrap items-end gap-3">
                    <div class="flex flex-col gap-1">
                        <span class="text-[11px] text-zzc-muted">rapportageperiode ({{ kpi.listPeriods().length }})</span>
                        <Segmented aria-label="rapportageperiode" :options="PERIODE_OPTIES" :model-value="String(kpi.getReportingPeriod())" @update:model-value="onPeriodChange"/>
                    </div>
                    <div class="flex flex-col gap-1" title="toepassingsperiode">
                        <span class="text-[11px] text-zzc-muted">toepassingsperiode</span>
                        <el-date-picker v-model="timeSpanHuman" type="monthrange" unlink-panels range-separator="tot" start-placeholder="start maand" end-placeholder="eind maand" :clearable="false"/>
                    </div>
                    <div class="flex flex-col gap-1 w-40" title="waarde type">
                        <span class="text-[11px] text-zzc-muted">waarde type</span>
                        <el-select v-model="kpi.waardeType" placeholder="waarde type" @change="onChange">
                            <el-option v-for="item in enum_values(WaardeTypeEnum)" :key="item" :label="WaardeTypeEnum_name(item)" :value="item"/>
                        </el-select>
                    </div>
                    <div class="flex flex-col gap-1 w-64" title="dashboard reeks">
                        <span class="text-[11px] text-zzc-muted">dashboard reeks</span>
                        <el-select v-model="kpi.dashboardReeks" placeholder="dashboard reeks" @change="onDashboardReeksChange" clearable>
                            <el-option v-for="item in Object.values(DashboardReeksEnum)" :key="item" :label="DashboardReeksEnum_name(item)" :value="item"/>
                        </el-select>
                    </div>
                    <div class="flex flex-col gap-1 w-48" title="prognoseschaal">
                        <span class="text-[11px] text-zzc-muted">prognoseschaal</span>
                        <el-select v-model="kpi.prognoseSchaal" placeholder="prognoseschaal" @change="onChange">
                            <el-option v-for="item in enum_values(PrognoseSchaalEnum)" :key="item" :label="VoorspellingsSchaalEnum_name(item)" :value="item"/>
                        </el-select>
                    </div>
                </div>

                <div v-if="!kpi.isMijlpaal()" class="flex flex-row flex-wrap items-center gap-x-5 gap-y-2">
                    <el-checkbox v-model="kpi.hasUitkeringsbedrag" label="uitkeringsbedrag & vecozo-id" @change="onChange"/>
                    <el-checkbox v-model="kpi.askKpiStatus" label="vraag KPI status" @change="onChange"/>
                    <el-checkbox v-model="kpi.hasDoelwaarde" label="doelwaarde" @change="onChange"/>
                    <el-checkbox v-model="kpi.isCalculatedValue" label="berekende waarde" @change="onChange"/>
                </div>

                <div v-if="!kpi.isMijlpaal() && !kpi.askKpiStatus" class="flex flex-row items-end gap-2" :title="dynExpressionTitle('gehaald als', kpi.gehaaldExpressionErrors)">
                    <div class="flex flex-col gap-1 flex-1">
                        <span class="text-[11px] text-zzc-muted">gehaald als</span>
                        <el-input v-model="kpi.gehaaldExpression" ref="gehaaldExpressionField" @input="onChange" @click="startEditingGehaaldExpression()" @focus="startEditingGehaaldExpression()" @blur="stopEditingGehaaldExpression()" type="textarea" placeholder="'gehaald als' expressie" :class="kpi.gehaaldExpressionErrors ? 'has-error' : ''"/>
                        <span v-if="kpi.gehaaldExpressionErrors" class="text-xs text-zzc-danger whitespace-pre-line">{{ kpi.gehaaldExpressionErrors }}</span>
                    </div>
                    <ExpressionEditorDialog
                        v-model="kpi.gehaaldExpression"
                        :errors="kpi.gehaaldExpressionErrors"
                        title="'gehaald als' expressie"
                        placeholder="'gehaald als' expressie"
                        :kpiNames="allKpiNames"
                        :instellingNames="instellingNames"
                        :themaNames="themaNames"
                        @input="onChange"
                    />
                </div>

                <div v-if="!kpi.isMijlpaal() && kpi.isCalculatedValue" class="flex flex-row items-end gap-2" :title="dynExpressionTitle('waarde', kpi.waardeExpressionErrors)">
                    <div class="flex flex-col gap-1 flex-1">
                        <span class="text-[11px] text-zzc-muted">waarde</span>
                        <el-input v-model="kpi.waardeExpression" ref="waardeExpressionField" @input="onChange" @click="startEditingWaardeExpression()" @focus="startEditingWaardeExpression()" @blur="stopEditingWaardeExpression()" type="textarea" placeholder="'waarde' expressie" :class="kpi.waardeExpressionErrors ? 'has-error' : ''"/>
                        <span v-if="kpi.waardeExpressionErrors" class="text-xs text-zzc-danger whitespace-pre-line">{{ kpi.waardeExpressionErrors }}</span>
                    </div>
                    <ExpressionEditorDialog
                        v-model="kpi.waardeExpression"
                        :errors="kpi.waardeExpressionErrors"
                        title="'waarde' expressie"
                        placeholder="'waarde' expressie"
                        :kpiNames="allKpiNames"
                        :instellingNames="instellingNames"
                        :themaNames="themaNames"
                        @input="onChange"
                    />
                </div>
            </div>
        </el-collapse-transition>
    </div>
</template>
